import {Trans} from '@lingui/macro';
import React from 'react';

import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {getChannelIconName} from '../../../../helpers/channelViewHelpers';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {ChannelModel} from '../../../../models/api/channelModel';
import {getFlatChannels} from '../../../../store/data/channels/channelsSelectors';
import {MultipleChoiceDropdown} from '../dropdowns/multipleChoiceDropdown';
import {SingleChoiceDropdown} from '../dropdowns/singleChoiceDropdown';
import {
  defaultDropdownWidth,
  GenericMultipleParameterProps,
  GenericSingleParameterProps,
} from './genericParameter';

/*
 * Types.
 */

interface ChannelParameterProps extends GenericSingleParameterProps<ChannelModel> {
  namespace: string;
}

interface ChannelsParameterProps extends GenericMultipleParameterProps<ChannelModel> {
  namespace: string;
}

/*
 * Component.
 */

export const ChannelParameter = ({namespace, ...rest}: ChannelParameterProps) => {
  const channels = useChannels(namespace);

  return (
    <SingleChoiceDropdown
      {...rest}
      items={channels}
      getItemKey={(item) => String(item.id)}
      renderItemLabel={(item) => item.displayName}
      renderItemIcon={(item) => <Icon name={getChannelIconName(item.contactSource)} />}
      serializeItemForSearch={(item) => item.displayName}
      width={defaultDropdownWidth}
      placeholder={<Trans id="rule-editor-select-channel">Select a channel</Trans>}
    />
  );
};

export const ChannelsParameter = ({namespace, ...rest}: ChannelsParameterProps) => {
  const channels = useChannels(namespace);

  return (
    <MultipleChoiceDropdown
      {...rest}
      items={channels}
      getItemKey={(item) => String(item.id)}
      renderItemLabel={(item) => item.displayName}
      renderItemIcon={(item) => <Icon name={getChannelIconName(item.contactSource)} />}
      serializeItemForSearch={(item) => item.displayName}
      placeholder={<Trans id="rule-editor-select-channel">Select a channel</Trans>}
      anyLabel={<Trans id="rule-editor-any-channel">Any channel</Trans>}
      anyIcon={<Icon name="channelCustomSmall" />}
      sortSelectedItemsToTop
      width={defaultDropdownWidth}
    />
  );
};

/*
 * Helpers.
 */

function useChannels(namespace: string) {
  return useAppSelector(getFlatChannels).filter((channel) => channel.namespace === namespace);
}
